<template>
  <transition name="ad-fade">
    <div v-if="visible" class="ad-overlay">
      <div class="ad-card">
        <!-- Photo preview -->
        <div class="photo-preview">
          <img :src="photoUrl" alt="Your photo" />
        </div>

        <!-- Pre-ad state -->
        <div v-if="!watching" class="ad-prompt">
          <p class="ad-message">Watch a short ad to save your photo</p>
          <button class="watch-btn" @click="onWatch">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            Watch Ad
          </button>
          <button class="dismiss-btn" @click="$emit('dismiss')">Cancel</button>
        </div>

        <!-- Ad playing state -->
        <div v-else class="ad-container">
          <div ref="adContainerEl" class="ad-slot"></div>
          <p v-if="adLoading" class="ad-loading">Loading ad...</p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref } from 'vue'
import { useAds } from '@/composables/useAds'

defineProps({
  visible: Boolean,
  photoUrl: String
})

const emit = defineEmits(['done', 'dismiss'])

const watching = ref(false)
const adContainerEl = ref(null)
const { adLoading, showRewardedAd } = useAds()

async function onWatch() {
  watching.value = true
  // Wait a tick for the container to render
  await new Promise(r => setTimeout(r, 50))
  if (adContainerEl.value) {
    await showRewardedAd(adContainerEl.value)
  }
  watching.value = false
  emit('done')
}
</script>

<style scoped>
.ad-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 20px;
}

.ad-card {
  background: #1e1e1e;
  border-radius: 16px;
  overflow: hidden;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.photo-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  overflow: hidden;
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(4px) brightness(0.7);
}

.ad-prompt {
  padding: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.ad-message {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.watch-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 32px;
  border-radius: 24px;
  border: none;
  background: #ff3b30;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.watch-btn:hover {
  background: #ff6259;
  transform: scale(1.03);
}

.dismiss-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  cursor: pointer;
  padding: 4px 12px;
}

.dismiss-btn:hover {
  color: rgba(255, 255, 255, 0.6);
}

.ad-container {
  padding: 16px;
  text-align: center;
}

.ad-slot {
  min-height: 280px;
  border-radius: 8px;
  overflow: hidden;
}

.ad-loading {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  margin-top: 8px;
}

.ad-fade-enter-active {
  transition: opacity 0.3s ease;
}
.ad-fade-leave-active {
  transition: opacity 0.2s ease;
}
.ad-fade-enter-from,
.ad-fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .ad-card {
    max-width: 100%;
    border-radius: 12px;
  }
  .ad-prompt {
    padding: 16px;
  }
  .ad-message {
    font-size: 14px;
  }
}
</style>
